import { motion } from "framer-motion";
import { CloudRain, Droplets, Leaf, BadgeCheck, Check } from "lucide-react";

const steps = [
  {
    icon: CloudRain,
    num: "01",
    title: "Atmospheric Harvesting",
    subtitle: "We gather the sky.",
    desc: "Humid air is drawn gently from the atmosphere — the cleanest source of water on earth — without disturbing a single river, well, or aquifer. As the air cools, moisture condenses exactly as morning dew forms on a leaf, giving us water at the very beginning of its journey.",
    points: ["Zero groundwater extraction", "Harvested at peak humidity for maximum purity"],
    span: "lg:col-span-2",
  },
  {
    icon: Droplets,
    num: "02",
    title: "Gentle Purification",
    subtitle: "Nothing but water.",
    desc: "A multi-stage journey of UV light, fine sediment filtration, activated carbon, and ozonation — removing everything that isn't pure water, gently and without harsh chemistry.",
    points: ["Four-stage purification", "No additives, ever"],
    span: "",
  },
  {
    icon: Leaf,
    num: "03",
    title: "Precise Mineral Enrichment",
    subtitle: "Nature's balance, restored.",
    desc: "Essential Magnesium and Calcium are reintroduced in precise ratios — the same minerals found in celebrated mountain springs — giving every bottle its smooth, refined taste.",
    points: ["Spring-grade mineral profile", "pH achieved through minerals alone"],
    span: "",
  },
  {
    icon: BadgeCheck,
    num: "04",
    title: "Uncompromising Quality",
    subtitle: "Zero compromise. Every bottle.",
    desc: "Every bottle is checked for fill, seal integrity, mineral balance, and pH before it leaves our facility. Batch after batch, season after season, what reaches you is exactly what nature intended — nothing more, nothing less.",
    points: ["Every batch verified for pH & minerals", "Sealed fresh in premium glass"],
    span: "lg:col-span-2",
  },
];

export const Technology = () => (
  <section id="innovation" data-testid="technology-section" className="py-24 md:py-32 lg:py-40 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
      >
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Our Craft</p>
        <h1 data-testid="technology-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
          From air to water. Naturally.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
          Four careful steps stand between the morning sky and your glass. Each one is
          designed around a single principle: remove nothing nature needs, add nothing
          your body doesn't.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {steps.map((s, i) => (
          <motion.article
            key={s.num}
            data-testid={`tech-card-${s.num}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
            className={`${s.span} group bg-[#F5F5F7] rounded-3xl p-8 lg:p-12 hover:-translate-y-1 transition-transform duration-300 ease-out`}
          >
            <div className="flex items-start justify-between mb-10">
              <s.icon className="w-8 h-8 text-[#2C3E35]" strokeWidth={1.5} />
              <span className="font-display text-5xl font-bold text-[#D2D2D7] group-hover:text-[#2C3E35]/30 transition-colors duration-300">
                {s.num}
              </span>
            </div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-3">{s.title}</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight font-bold text-[#1D1D1F] mb-4">{s.subtitle}</h3>
            <p className="text-base leading-relaxed text-[#86868B] max-w-2xl mb-6">{s.desc}</p>
            <ul className="space-y-2.5">
              {s.points.map((pt) => (
                <li key={pt} className="flex items-center gap-2.5 text-sm text-[#1D1D1F]/70">
                  <Check className="w-4 h-4 text-[#2C3E35]" strokeWidth={2} />
                  {pt}
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
